import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

// Validates a ZIP against the master US ZIP dataset (/api/locations/validate).
// Any real US ZIP is instantly usable — no admin approval needed.
export default function ZipInput({ value, onChange, onValid, placeholder = "e.g. 11215", testId, inputClassName = "edit-input" }) {
  const [status, setStatus] = useState("idle"); // idle | checking | valid | invalid
  const [info, setInfo] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const digits = (value || "").replace(/\D/g, "");
    if (digits.length !== 5) { setStatus("idle"); setInfo(null); return; }
    setStatus("checking");
    debounceRef.current = setTimeout(() => {
      api.get("/locations/validate", { params: { zip: digits } }).then(r => {
        if (r.data.valid) { setStatus("valid"); setInfo(r.data); onValid && onValid(r.data); }
        else { setStatus("invalid"); setInfo(null); }
      }).catch(() => { setStatus("invalid"); setInfo(null); });
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [value]);

  return (
    <div>
      <div className="relative">
        <input
          value={value || ""}
          onChange={e => onChange(e.target.value.replace(/\D/g, "").slice(0, 5))}
          maxLength={5} inputMode="numeric" placeholder={placeholder}
          className={inputClassName} style={{ paddingRight: "2rem" }}
          data-testid={testId}
        />
        {status === "checking" && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#222]/40"/>}
        {status === "valid" && <CheckCircle2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#245544]"/>}
        {status === "invalid" && <XCircle size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500"/>}
      </div>
      {status === "valid" && info && <p className="text-[11px] text-[#245544] mt-1" data-testid={`${testId}-resolved`}>{info.city}{info.city && info.state ? ", " : ""}{info.state}</p>}
      {status === "invalid" && <p className="text-[11px] text-red-500 mt-1" data-testid={`${testId}-error`}>That doesn't look like a valid U.S. ZIP code.</p>}
    </div>
  );
}
